<template>
  <div class="how-to-play">
    <h2 class="section-title large">{{ $t('howToPlay.title') }}</h2>
    <p class="subtitle">{{ $t('howToPlay.subtitle') }}</p>

    <div class="sections">
      <!-- Step 1: Select Your Game -->
      <section class="instruction-section">
        <div class="section-content image-first">
          <div class="image-content">
            <img :src="howToPlayStep1" alt="Select Your Game" />
          </div>
          
          <div class="text-content">
            <h3>{{ $t('howToPlay.step1Title') }}</h3>
            <p>{{ $t('howToPlay.step1Text1') }}</p>
            <p>{{ $t('howToPlay.step1Text2') }}</p>
            <p>{{ $t('howToPlay.step1Text3') }}</p>
          </div>
        </div>
      </section>

      <!-- Step 2: Select Draws -->
      <section class="instruction-section">
        <div class="section-content image-last">
          <div class="text-content">
            <h3>{{ $t('howToPlay.step2Title') }}</h3>
            <p>{{ $t('howToPlay.step2Text1') }}</p>
            <p>{{ $t('howToPlay.step2Text2') }}</p>
            <p>{{ $t('howToPlay.step2Text3') }}</p>
          </div>
          <div class="image-content">
            <img :src="howToPlayStep2" alt="Select Your Draws" />
          </div>
        </div>
      </section>

      <!-- Step 3: Select Market -->
      <section class="instruction-section">
        <div class="section-content image-first">
          <div class="image-content">
            <img :src="howToPlayStep3" alt="Select Your Market" />
          </div>
          <div class="text-content">
            <h3>{{ $t('howToPlay.step3Title') }}</h3>
            <p>{{ $t('howToPlay.step3Text1') }}</p>
            <p>{{ $t('howToPlay.step3Text2') }}</p>
            <p>{{ $t('howToPlay.step3Text3') }}</p>
          </div>
        </div>
      </section>

      <!-- Step 4: Confirm Your Entries -->
      <section class="instruction-section">
        <div class="section-content image-last">
          <div class="text-content">
            <h3>{{ $t('howToPlay.step4Title') }}</h3>
            <p>{{ $t('howToPlay.step4Text1') }}</p>
            <p>{{ $t('howToPlay.step4Text2') }}</p>
            <p>{{ $t('howToPlay.step4Text3') }}</p>
          </div>
          <div class="image-content">
            <img :src="howToPlayStep4" alt="Confirm Your Entries" />
          </div>
        </div>
      </section>

      <!-- Step 5: Your Betslip -->
      <section class="instruction-section">
        <div class="section-content image-first">
          <div class="image-content">
            <img :src="howToPlayStep5" alt="Your Betslip" />
          </div>
          <div class="text-content">
            <h3>{{ $t('howToPlay.step5Title') }}</h3>
            <p>{{ $t('howToPlay.step5Text1') }}</p>
            <p>{{ $t('howToPlay.step5Text2') }}</p>
            <p>{{ $t('howToPlay.step5Text3') }}</p>
          </div>
        </div>
      </section>

      <!-- Step 6: Wager Details -->
      <section class="instruction-section">
        <div class="section-content image-last">
          <div class="text-content">
            <h3>{{ $t('howToPlay.step6Title') }}</h3>
            <p>{{ $t('howToPlay.step6Text1') }}</p>
            <p>{{ $t('howToPlay.step6Text2') }}</p>
            <p>{{ $t('howToPlay.step6Text3') }}</p>
          </div>
          <div class="image-content">
            <img :src="howToPlayStep6" alt="Wager Details" />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { defineOptions } from 'vue'
import { useTheme } from '@/composables/useTheme'

defineOptions({
  name: 'HowToPlayView'
})

const { getAssetPath } = useTheme()

const howToPlayStep1 = computed(() => getAssetPath('howToPlay', 'step1') || '/img/images/how-to-play/1-select-game.png')
const howToPlayStep2 = computed(() => getAssetPath('howToPlay', 'step2') || '/img/images/how-to-play/2-select-draws.png')
const howToPlayStep3 = computed(() => getAssetPath('howToPlay', 'step3') || '/img/images/how-to-play/3-select-market.png')
const howToPlayStep4 = computed(() => getAssetPath('howToPlay', 'step4') || '/img/images/how-to-play/4-confirm-your-entries.png')
const howToPlayStep5 = computed(() => getAssetPath('howToPlay', 'step5') || '/img/images/how-to-play/5-your-betslip.png')
const howToPlayStep6 = computed(() => getAssetPath('howToPlay', 'step6') || '/img/images/how-to-play/6-wager-details.png')
</script>

<style scoped>
.how-to-play {
  max-width: 100%;
  margin: 0 auto;
}

.sections {
  display: flex;
  flex-direction: column;
}

.instruction-section {
  background: var(--bg-instruction-section, rgba(16, 21, 32, 0.05));
  padding: 0px !important;
}

.instruction-section h3 {
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
}

.section-content {
  display: grid;
  gap: 22px;
  align-items: start;
}

.section-content.image-first {
  grid-template-columns: 30% 70%;
}

.section-content.image-last {
  grid-template-columns: 70% 30%;
}

.text-content {
  color: var(--text-secondary);
  text-align: left;
  padding: 20px;
}

.text-content p {
  margin-bottom: 16px;
  line-height: 1.6;
}

.text-content p:last-child {
  margin-bottom: 0;
}

.image-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
  background: var(--bg-instruction-image, none);
}

.image-content img {
  width: 100%;
  max-width: 260px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  display: block;
  margin: 0;
}

@media (min-width: 769px) {
  .image-content {
    min-height: 180px;
  }
}

@media (max-width: 768px) {
  .section-content {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .image-content {
    order: 0 !important;
    margin: 0 auto 16px auto;
    max-width: 100%;
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .image-content img {
    width: 100%;
    max-width: 260px;
    height: auto;
    display: block;
    margin: 0 auto;
  }

  .instruction-section {
    padding: 20px 12px;
  }

  .instruction-section h3 {
    color: var(--text-primary);
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 18px;
    text-align: left;
    margin-top: 0;
  }

  .text-content {
    text-align: left;
    font-size: 15px;
    color: var(--text-primary);
    margin-top: 0;
  }

  .text-content p {
    margin-bottom: 12px;
    line-height: 1.6;
  }

  .text-content p:last-child {
    margin-bottom: 0;
  }

  .section-content.image-last .image-content {
    order: 0;
  }
  .section-content.image-last .text-content {
    order: 1;
  }
}
</style>